import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import VoiceDictation from '../components/VoiceDictation';
import { FileText, Plus, Search, X, RefreshCw, Clock, CheckCircle, AlertCircle, Save, ExternalLink, Syringe, Clipboard, UserCheck, Heart, Brain, Sparkles, FileSearch, Upload, FileImage, File, Loader2 } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const DocumentationPage = () => {
  const navigate = useNavigate();
  const [documentation, setDocumentation] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDictationModal, setShowDictationModal] = useState(false);
  const [dictationContent, setDictationContent] = useState('');
  const [selectedDocType, setSelectedDocType] = useState('nurses_notes');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    type: 'nurses_notes',
    assignedTo: '',
    dueDate: new Date().toISOString().split('T')[0]
  });
  
  // Document Upload State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [extractedContent, setExtractedContent] = useState('');
  const [targetTemplate, setTargetTemplate] = useState('nurses_notes');
  const fileInputRef = useRef(null);

  // Quick access forms for sidebar - all 7 clinical forms
  const quickForms = [
    { id: 'admission-consent', name: 'Admission Consent', icon: FileText, path: '/dashboard/forms/admission-consent', color: 'text-amber-600 bg-amber-50', description: 'Patient admission forms' },
    { id: 'nurses-notes', name: 'Nurses Notes', icon: Clipboard, path: '/dashboard/forms/nurses-notes', color: 'text-blue-600 bg-blue-50', description: 'Comprehensive visit notes' },
    { id: 'wound-care', name: 'Wound Care', icon: Heart, path: '/dashboard/forms/wound-care', color: 'text-red-600 bg-red-50', description: 'Wound tracking sheet' },
    { id: 'face-to-face', name: 'Face to Face', icon: UserCheck, path: '/dashboard/forms/face-to-face', color: 'text-green-600 bg-green-50', description: 'Physician certification' },
    { id: 'iv-therapy', name: 'IV Therapy', icon: Syringe, path: '/dashboard/forms/iv-therapy', color: 'text-purple-600 bg-purple-50', description: 'IV administration sheet' },
    { id: 'oasis', name: 'OASIS Assessment', icon: FileText, path: '/dashboard/forms/oasis', color: 'text-orange-600 bg-orange-50', description: 'SOC/ROC assessment' },
    { id: 'discharge', name: 'Discharge', icon: ExternalLink, path: '/dashboard/forms/discharge', color: 'text-teal-600 bg-teal-50', description: 'Discharge assessment' },
  ];

  const documentTypes = [
    { id: 'doctors_orders', name: "Doctor's Orders", icon: '📋', description: 'Physician orders and instructions' },
    { id: 'nurses_notes', name: 'Nurses Notes', icon: '👩‍⚕️', description: 'Skilled nursing visit documentation' },
    { id: 'hha_notes', name: 'Home Health Aide Notes', icon: '🏠', description: 'Home health aide visit notes' },
    { id: 'lvn_notes', name: 'LVN Notes', icon: '💉', description: 'Licensed vocational nurse notes' },
    { id: 'therapist_notes', name: 'Therapist Notes', icon: '🦿', description: 'PT/OT/ST therapy notes' },
    { id: 'oasis_assessment', name: 'OASIS Assessment', icon: '📝', description: 'Comprehensive OASIS documentation' }
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;

      const [docsRes, patientsRes] = await Promise.all([
        api.get('/documentation', { params }),
        api.get('/patients')
      ]);

      if (docsRes.data.success) {
        setDocumentation(docsRes.data.documentation);
      }
      if (patientsRes.data.success) {
        setPatients(patientsRes.data.patients);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocumentation = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/documentation', formData);
      if (response.data.success) {
        toast.success('Documentation task created');
        setShowAddModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error creating documentation:', error);
      toast.error('Failed to create documentation task');
    }
  };

  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      const response = await api.put(`/documentation/${docId}?status=${newStatus}`);
      if (response.data.success) {
        toast.success('Documentation status updated');
        fetchData();
      }
    } catch (error) {
      console.error('Error updating documentation:', error);
      toast.error('Failed to update documentation');
    }
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData({
        ...formData,
        patientId: patient.id,
        patientName: `${patient.firstName} ${patient.lastName}`
      });
    }
  };

  const openDictationModal = (docType) => {
    setSelectedDocType(docType);
    setDictationContent('');
    setSelectedPatient(null);
    setShowDictationModal(true);
  };

  const handleSaveDictation = () => {
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }
    if (!dictationContent.trim()) {
      toast.error('Please dictate or type some content');
      return;
    }
    
    toast.success(`${getDocTypeName(selectedDocType)} saved for ${selectedPatient.firstName} ${selectedPatient.lastName}`);
    setShowDictationModal(false);
    setDictationContent('');
    setSelectedPatient(null);
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      patientName: '',
      type: 'nurses_notes',
      assignedTo: '',
      dueDate: new Date().toISOString().split('T')[0]
    });
  };

  // Document Upload Functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (JPG, PNG, WEBP)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setUploadedFile(file);
      setExtractedContent('');
    }
  };

  const handleProcessDocument = async () => {
    if (!uploadedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploadProcessing(true);
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(uploadedFile);
      });

      // Send to AI for processing
      const response = await api.post('/ai/process-document', {
        file_data: base64,
        file_type: uploadedFile.type,
        file_name: uploadedFile.name,
        target_template: targetTemplate
      });

      if (response.data.success) {
        setExtractedContent(response.data.extracted_content);
        toast.success('Document processed successfully!');
      } else {
        toast.error(response.data.message || 'Failed to process document');
      }
    } catch (error) {
      console.error('Document processing error:', error);
      toast.error('Failed to process document. Please try again.');
    } finally {
      setUploadProcessing(false);
    }
  };

  const handleGenerateFromExtracted = () => {
    if (!extractedContent) {
      toast.error('Please process a document first');
      return;
    }
    // Navigate to the document generator with the extracted content
    navigate('/dashboard/document-generator', { 
      state: { 
        prefillContent: extractedContent,
        templateType: targetTemplate 
      } 
    });
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setExtractedContent('');
    setTargetTemplate('nurses_notes');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredDocs = documentation.filter((doc) =>
    doc.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.assignedTo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDocTypeName = (typeId) => {
    const type = documentTypes.find(t => t.id === typeId);
    return type ? type.name : typeId;
  };

  const getDocTypeIcon = (typeId) => {
    const type = documentTypes.find(t => t.id === typeId);
    return type ? type.icon : '📄';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const stats = {
    pending: documentation.filter(d => d.status === 'Pending').length,
    inProgress: documentation.filter(d => d.status === 'In Progress').length,
    completed: documentation.filter(d => d.status === 'Completed').length,
    overdue: documentation.filter(d => d.status === 'Overdue').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="documentation-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Documentation</h1>
            <p className="text-gray-600 mt-1">Clinical documentation and records</p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d] transition-colors"
            data-testid="new-document-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Document
          </button>
        </div>

        {/* AI Feature Buttons - Two Prominent Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* AI Auto Documentation Button */}
          <button
            onClick={() => navigate('/dashboard/document-generator')}
            className="group relative bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-8 text-left shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] overflow-hidden"
            data-testid="ai-auto-documentation-btn"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Brain className="w-10 h-10 text-white" />
                </div>
                <Sparkles className="w-6 h-6 text-amber-200 animate-pulse" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">AI AUTO DOCUMENTATION</h3>
              <p className="text-amber-100 text-sm leading-relaxed">
                Automatically generate Facesheets, Admission Forms, Consent Forms, SOC/ROC, 485 Plan of Care, Discharge Assessments, and more from clinical notes.
              </p>
              <div className="mt-4 flex items-center gap-2 text-white/80 text-sm">
                <span className="px-2 py-1 bg-white/20 rounded-full">Facesheet</span>
                <span className="px-2 py-1 bg-white/20 rounded-full">485 POC</span>
                <span className="px-2 py-1 bg-white/20 rounded-full">+10 more</span>
              </div>
            </div>
          </button>

          {/* AI Summary Generator Button */}
          <button
            onClick={() => navigate('/dashboard/forms/soc-roc')}
            className="group relative bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-8 text-left shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] overflow-hidden"
            data-testid="ai-summary-generator-btn"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="absolute top-4 right-4 px-2 py-1 bg-green-400 rounded-full text-xs text-white font-medium">
              Active
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                  <FileSearch className="w-10 h-10 text-white" />
                </div>
                <Sparkles className="w-6 h-6 text-purple-200 animate-pulse" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">AI SUMMARY GENERATOR</h3>
              <p className="text-purple-100 text-sm leading-relaxed">
                Generate comprehensive OASIS clinical summaries directly from assessment forms. AI-powered patient summary generation for clinical documentation.
              </p>
              <div className="mt-4 flex items-center gap-2 text-white/80 text-sm">
                <span className="px-2 py-1 bg-white/20 rounded-full">OASIS</span>
                <span className="px-2 py-1 bg-white/20 rounded-full">Clinical Summary</span>
                <span className="px-2 py-1 bg-white/20 rounded-full">Auto-Fill</span>
              </div>
            </div>
          </button>
        </div>

        {/* Document Upload Section - NEW */}
        <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 rounded-xl p-6 border border-teal-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500 rounded-lg">
                <Upload className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Upload & Auto-Generate</h2>
                <p className="text-sm text-gray-600">Upload PDFs or handwritten documents to auto-populate templates</p>
              </div>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2"
              data-testid="upload-document-btn"
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </button>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <File className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-gray-800">PDF Documents</span>
              </div>
              <p className="text-sm text-gray-600">Upload PDF files from scanned documents or digital forms</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <FileImage className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-gray-800">Handwritten Notes</span>
              </div>
              <p className="text-sm text-gray-600">Upload photos of handwritten clinical notes for AI extraction</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-gray-800">AI Auto-Fill</span>
              </div>
              <p className="text-sm text-gray-600">AI extracts content and populates your documentation templates</p>
            </div>
          </div>
        </div>

        {/* Voice Dictation Cards */}
        <div className="bg-gradient-to-r from-[#ea580c]/5 to-[#f59e0b]/5 rounded-xl p-6 border border-[#ea580c]/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#ea580c] rounded-lg">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Voice Dictation</h2>
              <p className="text-sm text-gray-600">Click on a document type to start voice dictation</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {documentTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => openDictationModal(type.id)}
                className="p-4 bg-white rounded-xl border border-gray-200 hover:border-[#ea580c] hover:shadow-md transition-all text-center group"
                data-testid={`dictation-${type.id}`}
              >
                <span className="text-2xl block mb-2">{type.icon}</span>
                <p className="text-sm font-medium text-gray-800 group-hover:text-[#ea580c]">{type.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Quick Access Forms Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Clinical Documentation Forms</h2>
              <p className="text-sm text-gray-600">Quick access to all digitized forms</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {quickForms.map((form) => {
              const Icon = form.icon;
              return (
                <button
                  key={form.id}
                  onClick={() => navigate(form.path)}
                  className={`p-4 rounded-xl border-2 border-transparent hover:border-[#ea580c] transition-all text-left group ${form.color}`}
                  data-testid={`quick-form-${form.id}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6" />
                  </div>
                  <p className="font-medium text-gray-800">{form.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{form.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl shadow-sm border border-yellow-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-2xl font-bold text-yellow-600">{stats.pending}</span>
            </div>
            <h3 className="font-medium text-gray-800">Pending</h3>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border border-blue-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats.inProgress}</span>
            </div>
            <h3 className="font-medium text-gray-800">In Progress</h3>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border border-green-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-2xl font-bold text-green-600">{stats.completed}</span>
            </div>
            <h3 className="font-medium text-gray-800">Completed</h3>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border border-red-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-2xl font-bold text-red-600">{stats.overdue}</span>
            </div>
            <h3 className="font-medium text-gray-800">Overdue</h3>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by patient or assignee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
              />
            </div>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c]"
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Overdue">Overdue</option>
            </select>
            <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg" title="Refresh">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800">Documentation Tasks</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-[#ea580c] mx-auto mb-4" />
              <p className="text-gray-500">Loading documentation...</p>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No documentation found</h3>
              <p className="text-gray-500 mb-4">Create documentation tasks to track clinical notes</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d]"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Document
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredDocs.map((doc) => (
                <div key={doc.id} className="p-6 hover:bg-gray-50" data-testid={`doc-row-${doc.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#ea580c] rounded-lg flex items-center justify-center flex-shrink-0 text-2xl">
                        {getDocTypeIcon(doc.type)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{doc.patientName}</h3>
                        <p className="text-sm text-gray-600">{getDocTypeName(doc.type)}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Assigned to: {doc.assignedTo} • Due: {new Date(doc.dueDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(doc.status)}`}>
                        {doc.status}
                      </span>
                      {doc.status !== 'Completed' && (
                        <select
                          value={doc.status}
                          onChange={(e) => handleUpdateStatus(doc.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#ea580c]"
                        >
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Documentation Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">New Documentation Task</h2>
                <button 
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddDocumentation} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                  <select
                    required
                    value={formData.patientId}
                    onChange={(e) => handlePatientSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    <option value="">Select a patient</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.firstName} {patient.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    {documentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.icon} {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <input
                    type="text"
                    required
                    value={formData.assignedTo}
                    onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                    placeholder="e.g., Nurse Jennifer Davis"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    required
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); resetForm(); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d]"
                  >
                    Create Task
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Voice Dictation Modal */}
        {showDictationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getDocTypeIcon(selectedDocType)}</span>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{getDocTypeName(selectedDocType)}</h2>
                    <p className="text-sm text-gray-600">Voice dictation enabled</p>
                  </div>
                </div>
                <button 
                  onClick={() => { setShowDictationModal(false); setDictationContent(''); setSelectedPatient(null); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Patient</label>
                  <select
                    value={selectedPatient?.id || ''}
                    onChange={(e) => {
                      const patient = patients.find(p => p.id === e.target.value);
                      setSelectedPatient(patient || null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    <option value="">Select a patient</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.firstName} {patient.lastName} - {patient.primaryDiagnosis}
                      </option>
                    ))}
                  </select>
                  {patients.length === 0 && (
                    <p className="text-sm text-orange-600 mt-1">No patients found. Add a patient first.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dictate Your Notes
                  </label>
                  <VoiceDictation 
                    onTranscript={(text) => setDictationContent(text)}
                    placeholder={`Start speaking to dictate your ${getDocTypeName(selectedDocType).toLowerCase()}...`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Edit Notes (optional)
                  </label>
                  <textarea
                    value={dictationContent}
                    onChange={(e) => setDictationContent(e.target.value)}
                    rows={6}
                    placeholder="You can also type or edit your notes here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setShowDictationModal(false); setDictationContent(''); setSelectedPatient(null); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveDictation}
                    disabled={!selectedPatient || !dictationContent.trim()}
                    className="flex-1 px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Save Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Upload className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Upload Document</h2>
                      <p className="text-teal-100 text-sm">Upload PDF or image for AI extraction</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowUploadModal(false); resetUpload(); }}
                    className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* File Upload Area */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Document
                  </label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                      uploadedFile ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-teal-400'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                    {uploadedFile ? (
                      <div className="space-y-2">
                        <div className="w-16 h-16 mx-auto bg-teal-100 rounded-full flex items-center justify-center">
                          {uploadedFile.type.includes('pdf') ? (
                            <File className="w-8 h-8 text-teal-600" />
                          ) : (
                            <FileImage className="w-8 h-8 text-teal-600" />
                          )}
                        </div>
                        <p className="font-medium text-gray-800">{uploadedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); resetUpload(); }}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="font-medium text-gray-800">Click to upload or drag and drop</p>
                        <p className="text-sm text-gray-500">PDF, JPG, PNG, or WEBP (max 10MB)</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Target Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Template
                  </label>
                  <select
                    value={targetTemplate}
                    onChange={(e) => setTargetTemplate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="target-template-select"
                  >
                    {documentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.icon} {type.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    The AI will extract information relevant to this template type
                  </p>
                </div>

                {/* Process Button */}
                <button
                  onClick={handleProcessDocument}
                  disabled={!uploadedFile || uploadProcessing}
                  className="w-full py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  data-testid="process-document-btn"
                >
                  {uploadProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Extract Content with AI
                    </>
                  )}
                </button>

                {/* Extracted Content Preview */}
                {extractedContent && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Extracted Content
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-60 overflow-y-auto">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                        {extractedContent}
                      </pre>
                    </div>
                    <button
                      onClick={handleGenerateFromExtracted}
                      className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 flex items-center justify-center gap-2 transition-colors"
                      data-testid="generate-from-extracted-btn"
                    >
                      <FileText className="w-5 h-5" />
                      Generate {getDocTypeName(targetTemplate)}
                    </button>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200">
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowUploadModal(false); resetUpload(); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DocumentationPage;
